/**
 * 端末IDの「他の端末が使っている」判定（docs/08 §230）。
 *
 * 原典のプロトコルは端末IDだけが識別子なので、他人と同じIDで送ると航跡が混ざる。そのため、受信中の
 * 端末に同じIDがあれば警告していた。ただし、自分が前に使ったID（端末に保存）は除いていた。
 *
 * ★ 2026-11-01、iPhoneのストレージ不足でアプリのデータが消え、保存していた自分のIDも消えた。
 *   同じIDを入れ直すと、Bridge に残っていた**自分の前日の航跡**を「他の端末」とみなして
 *   「既に他の端末が使っています。別のIDにしてください」と出た（利用者の報告）。
 *   **今まさに送っている端末**だけを「使っている」とみなす。最後の受信が古いIDは、送信をやめた端末
 *   （＝自分の前回のぶんであることが多い）なので警告しない。
 */

/** 最後の受信がこれより新しければ「今使っている」とみなす（送信間隔は最大600秒まで設定できるので、その倍に近い余裕） */
export const TERMINAL_ID_IN_USE_WINDOW_MS = 15 * 60 * 1000;

export interface TerminalIdUse {
  /** 今まさに他の端末が送っている（＝衝突する） */
  inUse: boolean;
  /** 同じIDの最後の受信時刻（UTCミリ秒）。同じIDが無ければ null */
  lastSeen: number | null;
}

export function terminalIdUse(
  id: string,
  terminals: ReadonlyArray<{ terminalId: string; latest: { t: number } }>,
  nowMs: number,
  ownId: string,
  windowMs = TERMINAL_ID_IN_USE_WINDOW_MS,
): TerminalIdUse {
  const trimmed = id.trim();
  if (!trimmed) return { inUse: false, lastSeen: null };
  const same = terminals.find((t) => t.terminalId === trimmed);
  if (!same) return { inUse: false, lastSeen: null };
  const fresh = nowMs - same.latest.t <= windowMs;
  return { inUse: fresh && trimmed !== ownId, lastSeen: same.latest.t };
}
